import React, { useMemo } from 'react';
import { Card, Descriptions, Tag, Space, Statistic, Row, Col, Alert } from 'antd';
import { DollarOutlined, CreditCardOutlined, PercentageOutlined } from '@ant-design/icons';
import { formatAmount, getActualLoanData, resolveEssPayload, toNumber } from './essPayload';

interface ESSFinancialInfoViewProps {
    data: any;
    formikProps?: any;
}

const ESSFinancialInfoView: React.FC<ESSFinancialInfoViewProps> = ({ data, formikProps }) => {
    const actualData = getActualLoanData(data, formikProps);

    // Parse ESS request payload
    const essPayload = useMemo(() => {
        return resolveEssPayload(actualData);
    }, [actualData?.essRequestPayload, actualData?.formattedEssRequestPayload]);

    if (!essPayload) {
        return null;
    }

    // Calculate affordability metrics
    const basicSalary = toNumber(essPayload.basicSalary);
    const netSalary = toNumber(essPayload.netSalary);
    const oneThirdAmount = toNumber(essPayload.oneThirdAmount);
    const deductibleAmount = toNumber(essPayload.deductibleAmount || essPayload.desiredDeductibleAmount);
    const totalDeductions = toNumber(essPayload.totalEmployeeDeduction);
    const requestedAmount = toNumber(essPayload.requestedAmount);
    const desiredDeductible = toNumber(essPayload.desiredDeductibleAmount || essPayload.deductibleAmount);

    const availableForDeduction = Math.max(netSalary - oneThirdAmount, 0);
    const remainingDeductionCapacity = Math.max(availableForDeduction - totalDeductions, 0);
    const utilizationRaw = availableForDeduction > 0 ? (totalDeductions / availableForDeduction) * 100 : 0;
    const projectedUtilizationRaw = availableForDeduction > 0
        ? ((totalDeductions + desiredDeductible) / availableForDeduction) * 100
        : 0;
    const deductionUtilization = utilizationRaw.toFixed(1);
    const projectedUtilization = projectedUtilizationRaw.toFixed(1);

    return (
        <Card 
            title={
                <Space>
                    <DollarOutlined />
                    <span>ESS Financial & Salary Information</span>
                </Space>
            }
            className="mb-4"
        >
            {/* Salary Overview */}
            <Row gutter={16} className="mb-4">
                <Col xs={24} sm={8}>
                    <Statistic 
                        title="Basic Salary" 
                        value={basicSalary}
                        precision={2}
                        prefix="TZS"
                        valueStyle={{ color: '#3f8600' }}
                    />
                </Col>
                <Col xs={24} sm={8}>
                    <Statistic 
                        title="Net Salary" 
                        value={netSalary}
                        precision={2}
                        prefix="TZS"
                        valueStyle={{ color: '#1890ff' }}
                    />
                </Col>
                <Col xs={24} sm={8}>
                    <Statistic 
                        title="One-Third Protected Amount" 
                        value={oneThirdAmount}
                        precision={2}
                        prefix="TZS"
                        valueStyle={{ color: '#cf1322' }}
                    />
                </Col>
            </Row>

            {/* Deduction Capacity Alert */}
            {parseFloat(deductionUtilization) > 80 && (
                <Alert
                    message="High Deduction Utilization"
                    description={`Employee is already using ${deductionUtilization}% of available deduction capacity. Please verify affordability.`}
                    type="warning"
                    showIcon
                    className="mb-4"
                />
            )}

            {desiredDeductible > remainingDeductionCapacity && (
                <Alert
                    message="Desired deduction exceeds remaining ESS capacity"
                    description={`Requested installment of TZS ${formatAmount(desiredDeductible)} exceeds remaining capacity of TZS ${formatAmount(remainingDeductionCapacity)}.`}
                    type="error"
                    showIcon
                    className="mb-4"
                />
            )}

            <Descriptions bordered column={{ xs: 1, sm: 2, md: 2 }} className="mb-4">
                <Descriptions.Item label="Employer (Vote Code)">
                    {essPayload.voteCode || 'N/A'}
                </Descriptions.Item>

                <Descriptions.Item label="Employer Name">
                    {essPayload.voteName || 'N/A'}
                </Descriptions.Item>

                <Descriptions.Item label="Total Existing Deductions">
                    <strong>TZS {formatAmount(totalDeductions)}</strong>
                </Descriptions.Item>

                <Descriptions.Item label="Remaining Deduction Capacity">
                    <strong>TZS {formatAmount(remainingDeductionCapacity)}</strong>
                </Descriptions.Item>

                <Descriptions.Item label="Deduction Utilization">
                    <Tag color={parseFloat(deductionUtilization) > 80 ? 'red' : parseFloat(deductionUtilization) > 60 ? 'orange' : 'green'}>
                        {deductionUtilization}%
                    </Tag>
                </Descriptions.Item>

                <Descriptions.Item label="Projected Utilization (After New Loan)">
                    <Tag color={parseFloat(projectedUtilization) > 100 ? 'red' : parseFloat(projectedUtilization) > 80 ? 'orange' : 'green'}>
                        {projectedUtilization}%
                    </Tag>
                </Descriptions.Item>
            </Descriptions>

            {/* Loan Request Details */}
            <Card 
                title={
                    <Space>
                        <CreditCardOutlined />
                        <span>Loan Request from ESS</span>
                    </Space>
                }
                type="inner"
                className="mb-4"
            >
                <Row gutter={16} className="mb-4">
                    <Col xs={24} sm={8}>
                        <Statistic 
                            title="Requested Amount" 
                            value={requestedAmount}
                            precision={2}
                            prefix="TZS"
                        />
                    </Col>
                    <Col xs={24} sm={8}>
                        <Statistic 
                            title="Desired Monthly Deduction" 
                            value={desiredDeductible}
                            precision={2}
                            prefix="TZS"
                        />
                    </Col>
                    <Col xs={24} sm={8}>
                        <Statistic 
                            title="Tenure" 
                            value={essPayload.tenure || 0}
                            suffix="months"
                        />
                    </Col>
                </Row>

                <Descriptions bordered column={{ xs: 1, sm: 2, md: 2 }}>
                    <Descriptions.Item label={<><PercentageOutlined /> Interest Rate</>}>
                        {essPayload.interestRate ? `${essPayload.interestRate}%` : 'N/A'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Processing Fee">
                        TZS {formatAmount(essPayload.processingFee)}
                    </Descriptions.Item>

                    <Descriptions.Item label="Insurance">
                        TZS {formatAmount(essPayload.insurance)}
                    </Descriptions.Item>

                    <Descriptions.Item label="Deductible Amount (ESS)">
                        TZS {formatAmount(deductibleAmount)}
                    </Descriptions.Item>

                    <Descriptions.Item label="Product Code">
                        <Tag>{essPayload.productCode || 'N/A'}</Tag>
                    </Descriptions.Item>
                </Descriptions>
            </Card>

            {/* Banking Details */}
            <Card 
                title="Banking & Disbursement Details"
                type="inner"
            >
                <Descriptions bordered column={{ xs: 1, sm: 2, md: 2 }}>
                    <Descriptions.Item label="Bank Account Number" span={2}>
                        <strong>{essPayload.bankAccountNumber || 'N/A'}</strong>
                    </Descriptions.Item>

                    <Descriptions.Item label="Nearest Branch Name">
                        {essPayload.nearestBranchName || 'N/A'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Nearest Branch Code">
                        {essPayload.nearestBranchCode || 'N/A'}
                    </Descriptions.Item>
                </Descriptions>
            </Card>
        </Card>
    );
};

export default ESSFinancialInfoView;
